// @catalog F.010
/**
 * Sub-componentes + helpers de presentación para ForecastProductRow.
 * Extraídos para mantener P7 (<500 LoC) en ForecastProductRow.tsx.
 *
 * Spec literal:
 *   - prototype/styles.css 407-464 (.num-bar + .dci-mini + .tag + .pill).
 */
import React from "react";
import { Box, Stack } from "@mui/material";
import type { Urgency } from "../../pages/forecast/selectors";

const mxn = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  maximumFractionDigits: 0,
});

export function formatAmountCompact(value: number): string {
  if (!Number.isFinite(value)) return "—";
  if (Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `$${Math.round(value / 1000)}K`;
  }
  return mxn.format(value);
}

export function urgencyToInvClass(u: Urgency): "crit" | "warn" | "" {
  if (u === "critical") return "crit";
  if (u === "warn") return "warn";
  return "";
}

function dciTone(
  score: number | null | undefined,
): "full" | "high" | "low" | "crit" {
  if (typeof score === "number") {
    if (score >= 0.9) return "full";
    if (score >= 0.7) return "high";
    if (score >= 0.4) return "low";
    return "crit";
  }
  return "crit";
}

export function demandLabel(
  c?: string | null,
): { label: string; tone: "good" | "warn" | "alert" | "" } {
  switch (c) {
    case "smooth":
      return { label: "Suave", tone: "good" };
    case "erratic":
      return { label: "Errática", tone: "warn" };
    case "intermittent":
      return { label: "Interm.", tone: "" };
    case "lumpy":
      return { label: "Lumpy", tone: "alert" };
    default:
      return { label: "—", tone: "" };
  }
}

export function MiniSparkline({ data }: { data: number[] }): React.ReactElement | null {
  const w = 60;
  const h = 14;
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const dx = w / (data.length - 1);
  const points = data.map((v, i) => `${i * dx},${h - (v / max) * h}`).join(" ");
  return (
    <Box component="svg" width={w} height={h} sx={{ display: "block" }} aria-hidden>
      <polyline points={points} fill="none" stroke="var(--ink-4)" strokeWidth={1.2} />
    </Box>
  );
}

export function NumBar({
  value,
  reorder,
  invClass,
}: {
  value: number;
  reorder: number | null | undefined;
  invClass: "crit" | "warn" | "";
}): React.ReactElement {
  const r = reorder ?? 0;
  const ratio = r > 0 ? Math.min(1, value / r) : 1;
  const fillColor =
    invClass === "crit"
      ? "var(--neg)"
      : invClass === "warn"
        ? "var(--warn)"
        : "var(--accent)";
  const valColor =
    invClass === "crit"
      ? "var(--neg)"
      : invClass === "warn"
        ? "var(--warn)"
        : "var(--ink-1)";
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        alignItems: "flex-end",
        minWidth: 70,
      }}
    >
      <Box
        sx={{
          fontWeight: 500,
          fontSize: 13,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          color: valColor,
        }}
      >
        {value.toLocaleString("es-MX")}
        <Box
          component="span"
          sx={{ color: "var(--ink-4)", fontWeight: 400, fontSize: 11, ml: "4px" }}
        >
          / {reorder ?? "—"}
        </Box>
      </Box>
      <Box
        sx={{
          width: 60,
          height: 4,
          bgcolor: "var(--bg-2)",
          borderRadius: "2px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${ratio * 100}%`,
            bgcolor: fillColor,
            borderRadius: "2px",
          }}
        />
      </Box>
    </Box>
  );
}

export function DciMini({
  score,
  count,
  target,
}: {
  score: number | null | undefined;
  count: number | null | undefined;
  target: number | null | undefined;
}): React.ReactElement {
  const pct = typeof score === "number" ? Math.max(0, Math.min(1, score)) : 0;
  const tone = dciTone(score);
  const fillColor =
    tone === "full"
      ? "var(--pos)"
      : tone === "high"
        ? "var(--accent)"
        : tone === "low"
          ? "var(--warn)"
          : "var(--neg)";
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="6px"
      sx={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}
    >
      <Box
        sx={{
          width: 36,
          height: 5,
          bgcolor: "var(--bg-2)",
          borderRadius: "3px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct * 100}%`,
            bgcolor: fillColor,
            borderRadius: "3px",
          }}
        />
      </Box>
      <Box component="span">
        {count ?? "—"}/{target ?? "—"}
      </Box>
    </Stack>
  );
}

export function Tag({
  children,
  tone = "",
  mono = false,
}: {
  children: React.ReactNode;
  tone?: "good" | "warn" | "alert" | "";
  mono?: boolean;
}): React.ReactElement {
  const palette = {
    good: { bg: "var(--pos-soft)", color: "var(--pos)", border: "transparent" },
    warn: { bg: "var(--warn-soft)", color: "var(--warn)", border: "transparent" },
    alert: { bg: "var(--neg-soft)", color: "var(--neg)", border: "transparent" },
    "": { bg: "var(--bg-2)", color: "var(--ink-2)", border: "var(--border)" },
  } as const;
  const p = palette[tone];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 7px",
        borderRadius: "4px",
        fontSize: 10.5,
        bgcolor: p.bg,
        color: p.color,
        border: `1px solid ${p.border}`,
        fontWeight: 500,
        whiteSpace: "nowrap",
        fontFamily: mono ? "var(--font-mono)" : "inherit",
      }}
    >
      {children}
    </Box>
  );
}

export function Pill({ active }: { active: boolean }): React.ReactElement {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 6px",
        borderRadius: "4px",
        fontSize: 10,
        fontWeight: 600,
        bgcolor: active ? "var(--pos-soft)" : "var(--bg-2)",
        color: active ? "var(--pos)" : "var(--ink-3)",
      }}
    >
      {active ? "ON" : "OFF"}
    </Box>
  );
}
